import { FORM_ERROR, Form, getDRFFormError, Submit, useFormState, type SubmissionErrors } from '@ff-it/form';
import type { RequestFailure } from '@ff-it/api';
import { Box, selectStyle, StylesConfig } from '@ff-it/ui';
import { useEffect } from 'react';
import type { ServiceCategory } from 'modules/supplier/outletOrServiceCategory/types';
import type { Vendor } from 'modules/supplier/vendor/types';
import { ServiceCategoryField } from 'modules/supplier/outletOrServiceCategory/OutletOrServiceCategoryField';
import { VendorField } from 'modules/supplier/vendor/VendorField';
import type { ActivityType } from 'types';
import { formatError } from 'utilities';
import * as styles from './ProvidedScopeEditor.css';

export type ProvidedScopeKind = 'vendor' | 'service_category';

export type ProvidedScopeValues = {
  provided_vendors: readonly number[];
  provided_service_categories: readonly number[];
};

type ScopeFormValues = {
  provided_vendors: readonly Vendor[];
  provided_service_categories: readonly Pick<ServiceCategory, 'id' | 'name'>[];
};

export type ProvidedScopeEditorProps = {
  activeType: ActivityType;
  vendors: readonly Vendor[];
  serviceCategories: readonly Pick<ServiceCategory, 'id' | 'name'>[];
  onSave: (scope: ProvidedScopeValues) => SubmissionErrors | void | Promise<SubmissionErrors | void>;
  onDirtyChange?: (dirty: boolean) => void;
  readOnly?: boolean;
};

function ids<T extends { id: number }>(items: readonly T[]): number[] {
  return items.map((item) => item.id);
}

function sameIds<T extends { id: number }>(left: readonly T[], right: readonly T[]): boolean {
  return left.length === right.length && left.every((item, index) => item.id === right[index]?.id);
}

function sameInitialValues(left: ScopeFormValues, right: ScopeFormValues): boolean {
  return (
    sameIds(left.provided_vendors, right.provided_vendors) &&
    sameIds(left.provided_service_categories, right.provided_service_categories)
  );
}

const scopeSelectStyles: StylesConfig<any, any, any> = {
  ...selectStyle,
  valueContainer: (base) => ({
    ...base,
    width: '100%',
  }),
  multiValue: (base) => ({
    ...base,
    justifyContent: 'space-between',
    width: '100%',
  }),
};

function ScopeFields({
  activeType,
  onDirtyChange,
  readOnly,
}: Pick<ProvidedScopeEditorProps, 'activeType' | 'onDirtyChange' | 'readOnly'>) {
  const { dirty, submitting } = useFormState<ScopeFormValues>({
    subscription: { dirty: true, submitting: true },
  });

  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  return (
    <>
      <VendorField
        name="provided_vendors"
        label="Provided vendors"
        filter={{ product_type: activeType }}
        isMulti
        disabled={readOnly || submitting}
        className={styles.field}
        isClearable={false}
        styles={scopeSelectStyles}
      />
      <ServiceCategoryField
        name="provided_service_categories"
        label="Provided service categories"
        filter={{ kind: 'SERVICE_CATEGORY', product_type: activeType }}
        isMulti
        closeMenuOnSelect={false}
        getOptionLabel={(serviceCategory) => serviceCategory.name}
        formatOptionLabel={(serviceCategory) => serviceCategory.name}
        getOptionValue={(serviceCategory) => String(serviceCategory.id)}
        disabled={readOnly || submitting}
        className={styles.field}
        isClearable={false}
        styles={scopeSelectStyles}
      />
      {!readOnly && (
        <Box mt="2">
          <Submit variant="primary" size="sm" disabled={!dirty}>
            Save Provided
          </Submit>
        </Box>
      )}
    </>
  );
}

export function ProvidedScopeEditor({
  activeType,
  vendors,
  serviceCategories,
  onSave,
  onDirtyChange,
  readOnly = false,
}: ProvidedScopeEditorProps) {
  const initialValues: ScopeFormValues = {
    provided_vendors: vendors,
    provided_service_categories: serviceCategories,
  };

  return (
    <Box as="section" className={styles.panel}>
      <Form<ScopeFormValues>
        initialValues={initialValues}
        initialValuesEqual={sameInitialValues}
        disabled={readOnly}
        noValidate
        onSubmit={async (values) => {
          try {
            return await onSave({
              provided_vendors: ids(values.provided_vendors),
              provided_service_categories: ids(values.provided_service_categories),
            });
          } catch (error) {
            return (
              getDRFFormError(error as RequestFailure<unknown>) ?? {
                [FORM_ERROR]: formatError(error) || 'Unable to save provided scope.',
              }
            );
          }
        }}
      >
        <ScopeFields activeType={activeType} onDirtyChange={onDirtyChange} readOnly={readOnly} />
      </Form>
    </Box>
  );
}
